import { Autocomplete, TextField } from '@mui/material';
import type { FormikProps } from 'formik';
import { useEffect, useRef } from 'react';
import { FormikTextField } from '../../components/formik-fields/text-field.js';

const HELPER_TEXT = 'Call sign used in NZ Police ECC log on';

interface Props<T extends { callSign: string }> {
  callSigns?: string[];
  formik: FormikProps<T>;
  restrictCallSigns?: boolean;
}

/**
 * Call sign entry. When the organization has configured call signs, these are
 * offered as options - free text is still allowed unless the organization
 * restricts patrols to the configured call signs.
 */
export function CallSignField<T extends { callSign: string }>({
  callSigns,
  formik,
  restrictCallSigns,
}: Props<T>): JSX.Element {
  const options = callSigns ?? [];
  const only = options.length === 1 ? options[0] : undefined;
  const { setFieldValue } = formik;
  const defaultApplied = useRef(false);

  // default to the only configured call sign, but only once, so the user can
  // still clear the field and enter their own
  useEffect(() => {
    if (!only || defaultApplied.current) return;
    defaultApplied.current = true;
    // don't clobber anything already entered while the options were loading
    if (formik.values.callSign) return;
    setFieldValue('callSign', only);
  }, [formik.values.callSign, only, setFieldValue]);

  if (!options.length) {
    return (
      <FormikTextField
        formik={formik}
        fullWidth
        helperText={HELPER_TEXT}
        id="callSign"
        inputProps={{ sx: { textTransform: 'uppercase' } }}
        label="Call Sign"
      />
    );
  }

  const freeSolo = !restrictCallSigns;

  return (
    <Autocomplete<string, false, boolean, boolean>
      autoSelect={freeSolo}
      disableClearable={!freeSolo}
      disabled={formik.isSubmitting}
      freeSolo={freeSolo}
      onChange={(_, value) =>
        formik.setFieldValue('callSign', (value ?? '').toLocaleUpperCase())
      }
      onInputChange={(_, value) => {
        if (!freeSolo) return;
        formik.setFieldValue('callSign', value.toLocaleUpperCase());
      }}
      options={options}
      renderInput={(params) => (
        <TextField
          {...params}
          error={
            formik.touched.callSign && Boolean(formik.errors.callSign as string)
          }
          fullWidth
          helperText={(formik.errors.callSign as string) || HELPER_TEXT}
          inputProps={{
            ...params.inputProps,
            sx: { textTransform: 'uppercase' },
          }}
          label="Call Sign"
          name="callSign"
          onBlur={formik.handleBlur}
        />
      )}
      value={formik.values.callSign || null}
    />
  );
}
